import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getSupabase } from "@/lib/hooks/api/utils";

export function useCreateCommunication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      contact_id: string;
      title?: string;
      body?: string;
      type_key?: string;
      type_id?: string;
      direction?: "inbound" | "outbound";
      status?: string;
      external_id?: string;
      occurred_at?: string;
      metadata?: Record<string, unknown>;
    }) => {
      const supabase = getSupabase();
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      let type_id = payload.type_id;
      if (!type_id && payload.type_key) {
        const { data: type } = await supabase
          .from("communication_types")
          .select("id")
          .eq("key", payload.type_key)
          .maybeSingle();
        type_id = type?.id;
      }

      const { data, error } = await supabase
        .from("communications")
        .insert([
          {
            contact_id: payload.contact_id,
            title: payload.title,
            body: payload.body,
            type_id,
            direction: payload.direction,
            status: payload.status,
            external_id: payload.external_id,
            occurred_at: payload.occurred_at || new Date().toISOString(),
            metadata: payload.metadata,
            user_id: user.id,
          },
        ])
        .select()
        .single();

      if (error) {
        console.error("Error creating communication:", error);
        throw new Error(error.message);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["communications"] });
      toast.success("Communication saved");
    },
    onError: () => toast.error("Failed to save communication"),
  });
}
